import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, MessageSquarePlus, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-neon-cyan/60 focus:ring-1 focus:ring-neon-cyan/40";

export default function SuggestionBox() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/suggestions", form);
      toast.success("Suggestion sent. We read every one.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8" data-testid="suggestion-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto max-w-2xl rounded-2xl border border-white/10 bg-[#131722] p-6 sm:p-10"
      >
        <div className="text-center">
          <MessageSquarePlus className="mx-auto h-7 w-7 text-neon-cyan" />
          <div className="mt-4 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">07 // Suggestions & Questions</div>
          <h2 className="mt-3 font-editorial text-2xl uppercase tracking-wide sm:text-3xl">Talk to the city</h2>
          <p className="mt-2 text-sm text-zinc-400">
            A spot we missed, a question about a night out, an idea for the guide — drop it here. Every message lands in a real inbox.
          </p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <textarea
            required
            rows={4}
            value={form.message}
            onChange={set("message")}
            placeholder="Your suggestion or question..."
            data-testid="suggestion-message-input"
            className={inputCls}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input value={form.name} onChange={set("name")} placeholder="Name (optional)" data-testid="suggestion-name-input" className={inputCls} />
            <input type="email" value={form.email} onChange={set("email")} placeholder="Email (optional, if you want a reply)" data-testid="suggestion-email-input" className={inputCls} />
          </div>
          <button type="submit" disabled={loading} data-testid="suggestion-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-neon-cyan py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {loading ? "Sending" : "Send It"}
          </button>
        </form>
      </motion.div>
    </section>
  );
}
